// ════════════════════════════════════════════════════════════════════════
// Icons.tsx — Librería compartida de íconos SVG de trazo simple
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Reemplazo sistemático de emoji por SVG
// Todo el sitio usaba emoji sueltos (🚀, ⚡, 🔧, etc.) como íconos rápidos.
// Un emoji se ve distinto en cada sistema operativo/navegador y lee como
// poco profesional. Estos son SVG con currentColor (heredan el color del
// texto/contenedor) y width/height: 1em (escalan con el tamaño de fuente),
// mismo lenguaje visual que components/Button/icons.tsx: trazo simple,
// sin relleno (salvo puntos pequeños), redondeado.
//
// Para campos de datos (arrays en landingData.ts / translations.ts) que
// antes guardaban el emoji como string, ahora guardan una clave semántica
// (ej: 'rocket', 'shield') y el componente <Icon name="rocket" /> hace el
// lookup en ICON_MAP. Para usos fijos en un solo lugar (Header, Footer,
// etc.) se importa el componente con nombre directamente.
// ════════════════════════════════════════════════════════════════════════

import type { SVGProps } from 'react';
import { LightningIcon } from '../Button/icons';

type IconProps = SVGProps<SVGSVGElement>;

// Re-exportado para que ICON_MAP tenga una entrada 'lightning' — el mismo
// ícono de rayo que ya usan las credenciales del Hero, así el ⚡ se ve
// igual en todo el sitio en vez de tener dos versiones distintas.
export { LightningIcon };

export function RocketIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 2.5c2.2 1.8 4 5 4 8.5 0 2.2-.7 4-1.5 5.5L12 19l-2.5-2.5C8.7 15 8 13.2 8 11c0-3.5 1.8-6.7 4-8.5z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="1.4" stroke="currentColor" strokeWidth={1.5} />
      <path
        d="M8.5 14.5 5 16l1-3.5M15.5 14.5 19 16l-1-3.5M10 19l-1 2.5M14 19l1 2.5"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function WrenchIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SparklesIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 3l1.4 4.6L18 9l-4.6 1.4L12 15l-1.4-4.6L6 9l4.6-1.4L12 3z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 15l.7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7L19 15z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LinkIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth={1.75} />
      <line
        x1="21"
        y1="21"
        x2="16.65"
        y2="16.65"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Ícono de sol — usado junto a MoonIcon en el botón de toggle de tema
// del Header (sol = modo claro, luna = modo oscuro).
export function SunIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle
        cx="12"
        cy="12"
        r="4.5"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <line
        x1="12"
        y1="1.5"
        x2="12"
        y2="4"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="20"
        x2="12"
        y2="22.5"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="1.5"
        y1="12"
        x2="4"
        y2="12"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="20"
        y1="12"
        x2="22.5"
        y2="12"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="4.4"
        y1="4.4"
        x2="6.1"
        y2="6.1"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="17.9"
        y1="17.9"
        x2="19.6"
        y2="19.6"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="4.4"
        y1="19.6"
        x2="6.1"
        y2="17.9"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="17.9"
        y1="6.1"
        x2="19.6"
        y2="4.4"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function DollarIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <line
        x1="12"
        y1="1"
        x2="12"
        y2="23"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <path
        d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FireIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 2c1 3 4 5 4 9a4 4 0 1 1-8 0c0-1 .3-2 .8-2.8.3.9 1.2 1.3 1.2 1.3-.6-2-.8-4.5 2-7.5z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function WarningIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="9"
        x2="12"
        y2="13"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="17"
        x2="12.01"
        y2="17"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function TrendDownIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <polyline
        points="23 18 13.5 8.5 8.5 13.5 1 6"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="17 18 23 18 23 12"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TrendUpIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <polyline
        points="23 6 13.5 15.5 8.5 10.5 1 18"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <polyline
        points="17 6 23 6 23 12"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CreditCardIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="1"
        y="4"
        width="22"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <line
        x1="1"
        y1="10"
        x2="23"
        y2="10"
        stroke="currentColor"
        strokeWidth={1.75}
      />
    </svg>
  );
}

export function BriefcaseIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="2"
        y="7"
        width="20"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <path
        d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FlaskIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M9 2v6.5L4.5 17a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 8.5V2"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="9"
        y1="2"
        x2="15"
        y2="2"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PencilIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 20h9"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={1.75} />
      <line
        x1="2"
        y1="12"
        x2="22"
        y2="12"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <path
        d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ConstructionIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M4 21V9l8-5v17M4 12h8M20 21V8l-8 1M14 21h10M7 21v-4M17 21v-4"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StarIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="3"
        y="11"
        width="18"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <path
        d="M7 11V7a5 5 0 0 1 10 0v4"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <line
        x1="16"
        y1="2"
        x2="16"
        y2="6"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="8"
        y1="2"
        x2="8"
        y2="6"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="3"
        y1="10"
        x2="21"
        y2="10"
        stroke="currentColor"
        strokeWidth={1.75}
      />
    </svg>
  );
}

export function XCircleIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={1.75} />
      <line
        x1="15"
        y1="9"
        x2="9"
        y2="15"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="9"
        y1="9"
        x2="15"
        y2="15"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CheckCircleIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={1.75} />
      <polyline
        points="8 12.5 11 15.5 16 9"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PlusCircleIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={1.75} />
      <line
        x1="12"
        y1="8"
        x2="12"
        y2="16"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="8"
        y1="12"
        x2="16"
        y2="12"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function BarChartIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <line
        x1="18"
        y1="20"
        x2="18"
        y2="10"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="20"
        x2="12"
        y2="4"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="6"
        y1="20"
        x2="6"
        y2="14"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function RobotIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="5"
        y="7"
        width="14"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth={1.75}
      />
      <circle cx="9" cy="13" r="1.1" fill="currentColor" />
      <circle cx="15" cy="13" r="1.1" fill="currentColor" />
      <path
        d="M9 17h6"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="7"
        x2="12"
        y2="3"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <circle cx="12" cy="2.4" r="1" fill="currentColor" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" {...props}>
      <line
        x1="18"
        y1="6"
        x2="6"
        y2="18"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <line
        x1="6"
        y1="6"
        x2="18"
        y2="18"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Lookup dinámico por clave semántica ─────────────────────────────────
// Para campos de datos (arrays en landingData.ts / translations.ts) que
// antes tenían el emoji como valor del string. Ahora ese string es una de
// estas claves y <Icon name="..." /> resuelve al componente SVG.
export const ICON_MAP = {
  rocket: RocketIcon,
  wrench: WrenchIcon,
  lightning: LightningIcon,
  shield: ShieldIcon,
  sparkles: SparklesIcon,
  link: LinkIcon,
  search: SearchIcon,
  moon: MoonIcon,
  sun: SunIcon,
  dollar: DollarIcon,
  fire: FireIcon,
  warning: WarningIcon,
  'trend-down': TrendDownIcon,
  'trend-up': TrendUpIcon,
  'credit-card': CreditCardIcon,
  briefcase: BriefcaseIcon,
  flask: FlaskIcon,
  pencil: PencilIcon,
  globe: GlobeIcon,
  construction: ConstructionIcon,
  star: StarIcon,
  lock: LockIcon,
  calendar: CalendarIcon,
  cross: XCircleIcon,
  check: CheckCircleIcon,
  new: PlusCircleIcon,
  'bar-chart': BarChartIcon,
  robot: RobotIcon,
  close: CloseIcon,
} as const;

export type IconName = keyof typeof ICON_MAP;

export function Icon({ name, ...props }: { name: IconName } & IconProps) {
  const Component = ICON_MAP[name];
  return <Component {...props} />;
}
